import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FaGithub,
  FaLinkedin,
  FaEnvelope,
  FaCode,
  FaPaintBrush,
  FaMobileAlt,
} from "react-icons/fa";

const Hero: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const adjustHeroHeight = () => {
      if (heroRef.current) {
        const navbar = document.querySelector("nav");
        const navbarHeight = navbar ? navbar.offsetHeight : 0;
        const windowHeight = window.innerHeight;
        heroRef.current.style.height = `${windowHeight - navbarHeight}px`;
        heroRef.current.style.marginTop = `${navbarHeight}px`;
      }
    };

    adjustHeroHeight();
    window.addEventListener("resize", adjustHeroHeight);

    return () => {
      window.removeEventListener("resize", adjustHeroHeight);
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  const floatingVariants = {
    hidden: { y: 0 },
    visible: {
      y: ["-10px", "10px"],
      transition: {
        yoyo: Infinity,
        duration: 2,
        ease: "easeInOut",
      },
    },
  };

  const name = "S. M. Shakib Hossain";

  const handleViewWork = () => {
    const projectsSection = document.getElementById("projects");
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.section
      ref={heroRef}
      id="hero"
      className="flex items-center justify-center relative overflow-hidden bg-gray-900 p-4 md:p-8" // Added padding
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Animated background elements */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2 }}
      >
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute bg-teal-400 rounded-full"
            style={{
              width: Math.random() * 20 + 5,
              height: Math.random() * 20 + 5,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50],
              opacity: [0, 0.7, 0],
            }}
            transition={{
              duration: Math.random() * 5 + 5,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
        ))}
      </motion.div>

      <div className="text-center z-10">
        <motion.h1
          className="text-6xl md:text-7xl font-bold mb-4 text-teal-400" // Responsive font size
          variants={itemVariants}
        >
          {name.split("").map((char, index) => (
            <motion.span
              key={`${char}-${index}`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: index * 0.05,
                type: "spring",
                damping: 12,
                stiffness: 200,
              }}
              style={{ display: "inline-block" }}
            >
              {char === " " ? "\u00A0" : char}
            </motion.span>
          ))}
        </motion.h1>
        <motion.p
          variants={itemVariants}
          className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed text-justify" // Responsive font size and justified text
        >
          Front-end Engineer with a flair for UI/UX, creating seamless and
          responsive interfaces. Skilled in front-end, with a solid grasp of
          backend development.
        </motion.p>
        <motion.div
          variants={itemVariants}
          className="flex justify-center space-x-6 mb-12"
        >
          <motion.a
            href="https://github.com/hossainsmshakib"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.2, rotate: 360 }}
            whileTap={{ scale: 0.9 }}
            className="text-gray-300 hover:text-teal-400 transition-colors duration-300"
          >
            <FaGithub size={32} />
          </motion.a>
          <motion.a
            href="https://linkedin.com/in/hossainsmshakib"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.2, rotate: 360 }}
            whileTap={{ scale: 0.9 }}
            className="text-gray-300 hover:text-teal-400 transition-colors duration-300"
          >
            <FaLinkedin size={32} />
          </motion.a>
          <motion.a
            href="mailto:hossainsmshakib00@gmail.com"
            whileHover={{ scale: 1.2, rotate: 360 }}
            whileTap={{ scale: 0.9 }}
            className="text-gray-300 hover:text-teal-400 transition-colors duration-300"
          >
            <FaEnvelope size={32} />
          </motion.a>
        </motion.div>
        <motion.button
          variants={itemVariants}
          whileHover={{
            scale: 1.05,
            boxShadow: "0px 0px 8px rgb(45, 212, 191)",
          }}
          whileTap={{ scale: 0.95 }}
          className="bg-teal-500 text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-teal-600 transition duration-300"
          onClick={handleViewWork}
        >
          View My Work
        </motion.button>
      </div>

      {/* Floating icons */}
      <motion.div
        className="absolute left-10 top-1/4"
        variants={floatingVariants}
        initial="hidden"
        animate="visible"
      >
        <FaCode size={48} className="text-teal-400 opacity-50" />
      </motion.div>
      <motion.div
        className="absolute right-10 top-1/3"
        variants={floatingVariants}
        initial="hidden"
        animate="visible"
      >
        <FaPaintBrush size={48} className="text-teal-400 opacity-50" />
      </motion.div>
      <motion.div
        className="absolute left-1/4 bottom-1/4"
        variants={floatingVariants}
        initial="hidden"
        animate="visible"
      >
        <FaMobileAlt size={48} className="text-teal-400 opacity-50" />
      </motion.div>
    </motion.section>
  );
};

export default Hero;
